import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, CheckCircle2, KeyRound } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { Logo } from '@/components/Shell';
import { Button, Field, Input, Spinner } from '@/components/ui';

/** Matches the rule the backend enforces in hashPassword(). */
const MIN_LENGTH = 10;

/**
 * Setting a password from a one-time link.
 *
 * Serves both a landlord's staff account and an operator's console account.
 * The two are the same screen and deliberately not the same endpoint: a token
 * issued for the console is worth every customer's data, and the backend
 * refuses to spend one of those at the staff route or the other way round
 * (see resetForToken_ in 10_RoutesAuth.gs). Passing the endpoint in keeps
 * that separation visible here rather than hidden in a shared handler.
 */
export default function ResetPassword({
  endpoint = '/auth/staff/reset-password',
  signInPath = '/login',
  forgotPath = '/forgot-password' as string | null,
  what = 'your PROVIA password',
}: {
  endpoint?: string;
  signInPath?: string;
  forgotPath?: string | null;
  what?: string;
} = {}) {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  /*
   * Checked before the form is shown. A link that expired overnight is a
   * common way to arrive here, and finding that out after typing a new
   * password twice is a small cruelty that costs one request to avoid.
   */
  const { data: check, loading: checking } = useApi<{
    valid: boolean; email?: string; full_name?: string;
  }>(token ? endpoint : null, { token });

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError('Those two passwords are not the same');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await api.post(endpoint, { token, password });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reset your password');
    } finally {
      setLoading(false);
    }
  }

  const shell = (children: ReactNode) => (
    <div className="min-h-screen flex items-center justify-center p-6 bg-bg mesh">
      <div className="w-full max-w-sm">
        <Link to={signInPath} className="flex items-center gap-2 text-sm text-muted mb-8 hover:text-ink w-fit">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
        <div className="flex items-center gap-2.5 mb-8">
          <Logo />
          <span className="font-extrabold tracking-tight text-lg">PROVIA</span>
        </div>
        {children}
      </div>
    </div>
  );

  if (checking) {
    return shell(
      <div className="flex items-center gap-3 text-muted text-sm">
        <Spinner className="h-4 w-4" /> Checking your link…
      </div>,
    );
  }

  if (!token || !check?.valid) {
    return shell(
      <>
        <h1 className="text-title">This link is no longer valid</h1>
        <p className="text-muted mt-3 text-sm leading-relaxed">
          Reset links expire after an hour and work only once. Ask for a new one and use the
          most recent email — any earlier link stops working as soon as you do.
        </p>
        <div className="mt-8 space-y-3">
          {/*
            The console has no self-service route to ask for one — another
            operator has to issue it — so there is no button to offer, and
            saying so beats a button that goes nowhere.
          */}
          {forgotPath ? (
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={() => navigate(forgotPath)}
            >
              Request a new link
            </Button>
          ) : (
            <p className="text-sm text-muted">
              Ask another operator to send you a new one from the console.
            </p>
          )}
          <p className="text-sm text-faint text-center">
            <Link to={signInPath} className="link">Back to sign in</Link>
          </p>
        </div>
      </>,
    );
  }

  if (done) {
    return shell(
      <>
        <div className="flex items-center gap-2.5">
          <CheckCircle2 className="h-5 w-5 text-ok" />
          <h1 className="text-title">Password changed</h1>
        </div>
        <p className="text-muted mt-3 text-sm leading-relaxed">
          Sign in with your new password. Any device that was already signed in stays signed
          in until its session runs out.
        </p>
        <Button
          variant="primary"
          size="lg"
          className="w-full mt-8"
          onClick={() => navigate(signInPath)}
        >
          Go to sign in
        </Button>
      </>,
    );
  }

  return shell(
    <>
      <div className="flex items-center gap-2.5">
        <KeyRound className="h-5 w-5 text-brand" />
        <h1 className="text-title">Choose a new password</h1>
      </div>
      <p className="text-muted mt-2 text-sm">
        {/* Says which password: the two accounts are not the same thing. */}
        This sets {what} for <span className="text-ink">{check.email}</span>.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4">
        {/*
          A hidden username field is what lets a password manager file the new
          password under the right account rather than as a stray entry.
        */}
        <input type="hidden" name="username" autoComplete="username" value={check.email ?? ''} />

        <Field label="New password" hint={`At least ${MIN_LENGTH} characters`}>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••"
            autoComplete="new-password"
            minLength={MIN_LENGTH}
            required
            autoFocus
          />
        </Field>

        <Field label="Confirm new password">
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••••"
            autoComplete="new-password"
            minLength={MIN_LENGTH}
            required
          />
        </Field>

        {error && (
          <div className="flex items-start gap-2 text-sm text-danger bg-danger/10
                          border border-danger/25 rounded-xl px-3 py-2.5">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" className="w-full" loading={loading}>
          Set new password
        </Button>
      </form>
    </>,
  );
}
